import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {AjaxErrorResponse, CheckoutStepProps} from '@Components/checkout-flow/checkout-flow.types';
import {CHECKOUT_REDIRECT_LINK_DISABLED_SECONDS, CheckoutMode, CREDIT_PACK_PURCHASE_ERROR_URI, SUPPORT_REQUEST_URL} from '@Components/checkout-flow/checkout-flow.types';
import {fetchCheckoutPollingIds, pollForCheckoutCompletion} from '@Components/checkout-flow/checkout-flow-thunk';
import {getFinalizingText, getFinalizingTitle, getPaymentFailedTitle} from '@Components/checkout-flow/checkout-flow.library';
import {handleCheckoutSuccess, redirectTo} from '@Components/checkout-flow/checkout-flow-completion.library';
import {FINALIZING_ERROR_STATE_INITIAL, type FinalizingErrorState, REDIRECT_LINK_SELECTOR} from '@Components/checkout-flow/components/finalizing-step/finalizing-step.types';
import styles from './finalizing-step.module.scss';

const MILLISECONDS_PER_SECOND = 1000;

export function FinalizingStep({request, controller}: CheckoutStepProps): ReactElement {
  const dispatch = useAppDispatch();
  const mode = useAppSelector((state) => state.checkoutFlow.mode);
  const stripeSession = useAppSelector((state) => state.checkoutFlow.stripeSession);
  const pollingIds = useAppSelector((state) => state.checkoutFlow.pollingIds);
  const [errorState, setErrorState] = useState<FinalizingErrorState>(FINALIZING_ERROR_STATE_INITIAL);
  const [isRedirectLinkEnabled, setIsRedirectLinkEnabled] = useState(false);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const hasStartedRef = useRef(false);
  const isMountedRef = useRef(false);
  const isTrial = request.mode === CheckoutMode.SUBSCRIPTION && request.isTrial;

  useEffect(() => {
    isMountedRef.current = true;
    if (hasStartedRef.current || stripeSession === null || mode === null) {
      return (): void => {
        isMountedRef.current = false;
      };
    }
    hasStartedRef.current = true;

    const finalize = async (): Promise<void> => {
      let ids;
      try {
        ids = await dispatch(fetchCheckoutPollingIds({sessionId: stripeSession.sessionId, isTrial})).unwrap();
      } catch (error) {
        if (isMountedRef.current) {
          failWith(getIdsErrorMessage(error));
        }
        return;
      }

      try {
        const result = await pollForCheckoutCompletion({mode, pollingIds: ids});
        // The charge is already authorised by this point, so the completion itself has to run even if the
        // panel has gone — closing the dialog while it finalises must not cost the buyer the download,
        // the credits or the purchase tracking. Only the handlers that touch this panel are stood down.
        handleCheckoutSuccess({
          request,
          effectiveMode: mode,
          result,
          handlers: {
            showPremiumConfirmation: (subscriptionResult): void => {
              if (isMountedRef.current) {
                controller.showPremiumConfirmation(subscriptionResult);
              }
            },
            showError: (message): void => {
              if (isMountedRef.current) {
                failWith(message);
              }
            },
            exit: (): void => {
              if (isMountedRef.current) {
                controller.exit();
              }
            },
          },
        });
      } catch (error) {
        if (isMountedRef.current) {
          failWith(getPollingErrorMessage(error));
        }
      }
    };

    void finalize();

    return (): void => {
      isMountedRef.current = false;
    };
  }, [stripeSession, mode]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setIsRedirectLinkEnabled(true);
    }, CHECKOUT_REDIRECT_LINK_DISABLED_SECONDS * MILLISECONDS_PER_SECOND);
    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const link = textContainerRef.current?.querySelector(REDIRECT_LINK_SELECTOR);
    if (link === null || link === undefined) {
      return;
    }
    link.classList.toggle('disabled', !isRedirectLinkEnabled);
  }, [isRedirectLinkEnabled, pollingIds]);

  const failWith = (message: string): void => {
    if (mode === CheckoutMode.CREDIT_PACK) {
      redirectTo(CREDIT_PACK_PURCHASE_ERROR_URI);
      return;
    }
    setErrorState({hasError: true, message});
  };

  if (errorState.hasError) {
    return (
      <div className={styles.container}>
        <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_48} type={IconType.FLAT} flatIconType={{color: FlatIconColor.DANGER, type: FlatIconColorType.DARK}} ariaHidden />
        <Text val={getPaymentFailedTitle(request)} size={TextSize.LG} bold className={`content-heading ${styles.title}`} />
        <Text val={errorState.message} size={TextSize.LG} className="content-body" dangerouslySetInnerHTML />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <CircularProgressLoader size={LOADER_SIZE.LARGE} classes="_unmargin" />
      <Text val={getFinalizingTitle(mode)} size={TextSize.LG} bold className={`content-heading ${styles.title}`} dangerouslySetInnerHTML />
      <div ref={textContainerRef} className={`js-loading-text ${styles.redirectText}`}>
        <Text val={getFinalizingText(mode, pollingIds)} size={TextSize.LG} className="content-body" dangerouslySetInnerHTML />
      </div>
    </div>
  );
}

const getIdsErrorMessage = (error: unknown): string => {
  const message = getAjaxErrorMessage(error);
  return message !== '' ? message : window.i18next.t('pmwjs_an_unexpected_error_occurred_contact_support', {link: SUPPORT_REQUEST_URL});
};

const getPollingErrorMessage = (error: unknown): string => {
  const message = getAjaxErrorMessage(error);
  return message !== '' ? message : window.i18next.t('pmwjs_try_again_or_contact_support_if_problem_persists');
};

const getAjaxErrorMessage = (error: unknown): string => {
  if (typeof error !== 'object' || error === null) {
    return '';
  }
  const ajaxError = error as AjaxErrorResponse;
  return ajaxError.data?.msg ?? '';
};
